<template>
    <div class="checkout__payment">

        <h2 class="checkout__section-title">Payment</h2>

        <!-- Saved cards radio (заживёт с auth; до тех пор массив пуст) -->
        <div v-if="savedCards.length" class="ps-saved">

            <label
                v-for="card in savedCards"
                :key="card.id"
                class="ps-card"
                :class="{ 'ps-card--active': selectedMethodId === card.id }"
            >
                <input v-model="selectedMethodId" :value="card.id" type="radio" class="ps-card__radio">
                <span class="ps-card__info">
                    <span class="ps-card__brand">
                        {{ formatBrand( card.brand ) }} <span class="ps-card__dots">••••</span> {{ card.last4 }}
                    </span>
                    <span class="ps-card__exp">
                        <template v-if="card.holder">{{ card.holder }} · </template>Expires {{ padMonth( card.exp_month ) }}/{{ String( card.exp_year ).slice( -2 ) }}
                    </span>
                </span>
            </label>

            <label class="ps-card ps-card--new" :class="{ 'ps-card--active': selectedMethodId === '' }">
                <input v-model="selectedMethodId" value="" type="radio" class="ps-card__radio">
                <span class="ps-card__info">
                    <span class="ps-card__brand">Use new payment method</span>
                </span>
            </label>
        </div>

        <!-- Stripe Payment Element: методы из Dashboard settings -->
        <div v-show="selectedMethodId === ''" class="checkout__stripe">

            <div v-if="! stripeReady" class="checkout__stripe-loading">
                <template v-if="initFailed">
                    <p class="checkout__error">
                        Couldn't load the payment form. Please disable ad/privacy blockers and try again.
                    </p>
                    <button type="button" class="btn btn--ghost checkout__retry" @click="retry">Retry</button>
                </template>
                <template v-else>
                    <span class="checkout__spinner"></span>
                    <span>Loading payment methods…</span>
                </template>
            </div>

            <div ref="paymentElement"></div>
            <!-- Имя держателя - НАШЕ поле (у Payment Element для карт
                 поля name нет, канон stripe_payment_element.md).
                 Классы тракта: высота/фокус зеркалят .Input элемента -->
            <div v-show="stripeReady" class="checkout__field" style="margin-top: 14px;">
                <label class="checkout__label" for="subCardName">Name on card</label>
                <input
                    id="subCardName"
                    v-model="cardName"
                    class="checkout__input"
                    type="text"
                    autocomplete="cc-name"
                    placeholder="Name as printed on card"
                >
            </div>

            <p v-if="cardError" class="checkout__error">{{ cardError }}</p>
        </div>

    </div>
</template>

<script>
import { loadStripeJs } from '../../js/plugins/stripe';

/**
 * PaymentStripeSubscribe (m1) - порт eComzy PaymentStripeSubscribe.vue.
 *
 * Механика подписки (канон донора):
 * - trial -> Elements mode 'setup' (без amount) -> /subscribe/intent отдаёт
 *   SetupIntent -> confirmSetup (сегодня $0, карта на сбор);
 * - paid  -> mode 'payment' + amount -> PaymentIntent -> confirmPayment;
 * - subscription_type ('setup'|'payment') решает БЭК в intent-ответе,
 *   фронт ветвится по нему, не по tier;
 * - succeeded ИЛИ requires_capture -> редирект на success/{hash}.
 *
 * Отличия от донора:
 * - setupFutureUsage в camelCase (донор писал snake - по смоку 19.07
 *   зеркало front<->intent живёт на camel);
 * - successUrl - проп (у донора window.baseURL, у нас канон data-атрибутов);
 * - GTM снят (контура нет на m1);
 * - appearance - палитра Spark (orange #FF6B35), унаследована от
 *   PaymentStripe m1;
 * - product_slug/landing_slug - задел bundle-режима, пока ''.
 *
 * 27.07 - канон чистой карточной формы (stripe_payment_element.md,
 * зеркалом Add card в ЛК):
 * - инпут "Name on card" над элементом + fields name:'never' +
 *   name в confirmParams обеих веток (holder-цепочка: вебхук attached
 *   несёт billing_details.name -> art_user_payment_methods.holder);
 * - terms card:'never' (мандат-текст), wallets never (форма чисто
 *   карточная; с кошельками name:'never' конфликтует);
 * - holder в радио сохранёнок (GatewayResolver отдаёт).
 */
export default {
    name: 'PaymentStripeSubscribe',

    props: {
        publicKey   : { type: String,  required: true },
        savedCards  : { type: Array,   default: () => [] },
        amount      : { type: Number,  required: true },               // центы (paid-mode)
        slug        : { type: String,  required: true },               // slug плана каталога
        tier        : { type: String,  required: true },               // 'trial' | 'monthly' | ...
        contact     : { type: Object,  default: () => ({}) },
        intentUrl   : { type: String,  default: '/subscribe/intent' },
        isAuth      : { type: Boolean, default: false },
        successUrl  : { type: String,  required: true },               // база + hash доклеим
        productSlug : { type: String,  default: '' },                  // задел bundle
        landingSlug : { type: String,  default: '' },                  // задел bundle
    },

    emits: [ 'error', 'loading' ],

    data() {
        return {
            stripe           : null,
            elements         : null,
            paymentElement   : null,
            selectedMethodId : '',
            stripeReady      : false,
            initFailed       : false,
            cardError        : '',
            cardName         : '',
        };
    },

    computed: {

        // trial -> Elements 'setup' (без amount); paid -> 'payment' + amount
        isTrial() {
            return this.tier === 'trial';
        },
    },

    async mounted() {

        this.selectedMethodId = this.savedCards.length > 0 ? this.savedCards[0].id : '';

        await this.initPayment();
    },

    beforeUnmount() {
        clearTimeout( this._readyTimer );
    },

    watch: {

        selectedMethodId( newVal, oldVal ) {

            if( ! this.stripe || ! this.elements )
                return;

            const wasNew = oldVal === '';
            const isNew  = newVal === '';

            this.cardError = '';

            if( isNew && ! wasNew ) {
                this.$nextTick( () => this.mountPaymentElement() );
            } else if( ! isNew && wasNew && this.paymentElement ) {
                this.paymentElement.unmount();
            }
        },
    },

    methods: {

        formatBrand( brand ) {

            const map = {
                visa       : 'VISA',
                mastercard : 'Mastercard',
                amex       : 'AMEX',
                discover   : 'Discover',
                diners     : 'Diners Club',
                jcb        : 'JCB',
                unionpay   : 'UnionPay',
            };

            return map[ ( brand || '' ).toLowerCase() ] || 'Card';
        },

        padMonth( m ) {
            return String( m ).padStart( 2, '0' );
        },

        // Динамическая загрузка Stripe.js с ретраями (донор дословно:

        setupStripe() {

            if( typeof window.Stripe !== 'function' )
                throw new Error( 'Failed to load Stripe.js' );

            this.stripe = window.Stripe( this.publicKey, { locale: 'en' } );

            const elementsOptions = {
                currency              : 'usd',
                paymentMethodCreation : 'manual',
                payment_method_types  : [ 'card' ],

                fonts : [{
                    cssSrc: 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap',
                }],

                appearance: {
                    theme: 'stripe',

                    // Палитра Spark (checkout.scss: --orange #FF6B35)
                    variables: {
                        colorPrimary         : '#FF6B35',
                        colorBackground      : '#FFFFFF',
                        colorText            : '#1A1A2E',
                        colorTextSecondary   : '#6B7280',
                        colorTextPlaceholder : '#9CA3AF',
                        colorDanger          : '#E5484D',

                        fontFamily     : '"DM Sans", system-ui, sans-serif',
                        fontSizeBase   : '14px',
                        borderRadius   : '10px',
                        spacingUnit    : '4px',
                        focusBoxShadow : '0 0 0 3px rgba(255, 107, 53, 0.15)',
                        focusOutline   : 'none',
                    },

                    rules: {
                        '.Input': {
                            border          : '1px solid #E5E7EB',
                            boxShadow       : 'none',
                            padding         : '12px 14px',
                            transition      : 'border-color 0.15s ease',
                        },
                        '.Input:focus': {
                            border    : '1px solid #FF6B35',
                            boxShadow : '0 0 0 3px rgba(255, 107, 53, 0.15)',
                        },
                        '.Input--invalid': {
                            border : '1px solid #E5484D',
                        },
                        // Зеркало .checkout__label - лейблы страйпа
                        // не отличить от наших (единая форма)
                        '.Label': {
                            fontWeight   : '700',
                            fontSize     : '12px',
                            color        : '#334155',
                            marginBottom : '5px',
                        },
                        '.Tab': {
                            border       : '1px solid #E5E7EB',
                            borderRadius : '10px',
                            padding      : '12px',
                            transition   : 'all 0.15s ease',
                        },
                        '.Tab:hover': {
                            border : '1px solid #FF6B35',
                        },
                        '.Tab--selected': {
                            border    : '1px solid #FF6B35',
                            boxShadow : 'inset 0 0 0 1px #FF6B35',
                        },
                        '.Error': {
                            fontSize  : '13px',
                            marginTop : '6px',
                        },
                    },
                },
            };

            // Ветка режима (канон донора): trial без amount и без
            // setupFutureUsage (бэк ставит save_default_payment_method =
            // 'on_subscription'); paid - amount + camelCase setupFutureUsage
            // (донор писал snake - camel по смоку 19.07)
            if( this.isTrial ) {
                elementsOptions.mode = 'setup';
            } else {
                elementsOptions.mode             = 'payment';
                elementsOptions.amount           = this.amount;
                elementsOptions.setupFutureUsage = 'off_session';
            }

            this.elements = this.stripe.elements( elementsOptions );

            // Чистая карточная форма (канон stripe_payment_element.md):
            // terms/wallets под нож, name - НАШЕ поле над элементом
            // (never = обязанность передать в confirmParams)
            this.paymentElement = this.elements.create( 'payment', {
                layout : 'tabs',
                terms  : { card: 'never' },
                fields : {
                    billingDetails : {
                        name    : 'never',
                        address : {
                            country    : 'auto',
                            postalCode : 'auto',
                        },
                    },
                },
                wallets : {
                    applePay  : 'never',
                    googlePay : 'never',
                },
                defaultValues : {
                    billingDetails : {
                        email   : this.contact.email || '',
                        address : {
                            country : this.contact.country || 'US',
                        },
                    },
                },
            });

            if( this.selectedMethodId === '' )
                this.$nextTick( () => this.mountPaymentElement() );
            else
                this.stripeReady = true;
        },

        async initPayment() {

            this.initFailed = false;

            try {
                await loadStripeJs();
                this.setupStripe();
            } catch( e ) {
                this.initFailed = true;
                this.$emit( 'error', e.message || 'Failed to load payment system' );
            }
        },

        retry() {

            clearTimeout( this._readyTimer );

            this.stripeReady = false;
            this.initFailed  = false;

            try { this.paymentElement?.unmount(); } catch( e ) { /* noop */ }

            this.paymentElement = null;
            this.elements       = null;
            this.stripe         = null;

            this.initPayment();
        },

        mountPaymentElement() {

            if( ! this.paymentElement || ! this.$refs.paymentElement )
                return;

            this.paymentElement.mount( this.$refs.paymentElement );

            // Watchdog донора: Elements заблокирован (adblock/CORS) -
            // 'ready' не придёт, спиннер повиснет. 12с -> Retry.
            clearTimeout( this._readyTimer );
            this._readyTimer = setTimeout( () => {
                if( ! this.stripeReady ) {
                    this.initFailed = true;
                    this.$emit( 'error', 'Payment form is blocked. Disable ad/privacy blockers and retry.' );
                }
            }, 12000 );

            this.paymentElement.on( 'ready', () => {
                clearTimeout( this._readyTimer );
                this.stripeReady = true;
                this.initFailed  = false;
            });
            this.paymentElement.on( 'change', ( event ) => {
                this.cardError = event.error?.message || '';
            });
        },

        // Public: родитель зовёт через $refs.payment.submit()
        async submit() {

            this.$emit( 'loading', true );
            this.cardError = '';

            try {

                // Валидация (только new method): имя держателя + Elements
                if( this.selectedMethodId === '' ) {

                    if( ! this.cardName.trim() ) {
                        this.cardError = 'Enter the name on card';
                        this.$emit( 'loading', false );
                        return;
                    }

                    const { error: submitError } = await this.elements.submit();
                    if( submitError ) {
                        this.cardError = submitError.message;
                        this.$emit( 'loading', false );
                        return;
                    }
                }

                // Create Subscription + Setup/PaymentIntent на бэке
                const response = await this.$http.post( this.intentUrl, {
                    slug              : this.slug,
                    contact           : this.contact,
                    payment_method_id : this.selectedMethodId,
                    product_slug      : this.productSlug,
                    landing_slug      : this.landingSlug,
                });
                const { status, data, message } = response?.data || {};

                if( status !== 'success' || ! data?.payment )
                    throw new Error( message || data?.message || 'Intent creation failed' );

                if( data.payment.type !== 'inline' || data.payment.gateway !== 'stripe' )
                    throw new Error( 'Unexpected gateway response' );

                const clientSecret     = data.payment.client_secret;
                const subscriptionType = data.payment.subscription_type; // 'setup' | 'payment'
                const successUrl       = this.successUrl + data.hash;

                if( ! [ 'setup', 'payment' ].includes( subscriptionType ) )
                    throw new Error( 'Unknown subscription_type: ' + subscriptionType );

                let result;

                if( subscriptionType === 'setup' ) {

                    // Trial flow -> подтверждаем SetupIntent
                    if( this.selectedMethodId !== '' ) {

                        // Сохранёнка -> attach для будущего charge
                        result = await this.stripe.confirmCardSetup( clientSecret, {
                            payment_method : this.selectedMethodId,
                        });

                    } else {

                        // Новый метод -> Elements + подтверждение setup
                        result = await this.stripe.confirmSetup({
                            elements      : this.elements,
                            clientSecret  : clientSecret,
                            confirmParams : {
                                return_url          : successUrl,
                                payment_method_data : {
                                    billing_details : {
                                        email : this.contact.email || '',
                                        name  : this.cardName.trim(),
                                    },
                                },
                            },
                            redirect : 'if_required',
                        });
                    }

                } else {

                    // Paid flow -> подтверждаем PaymentIntent
                    if( this.selectedMethodId !== '' ) {

                        // Сохранёнка
                        result = await this.stripe.confirmCardPayment( clientSecret, {
                            payment_method : this.selectedMethodId,
                        });

                    } else {

                        // Новый метод (карта; кошельки/Link отключены)
                        result = await this.stripe.confirmPayment({
                            elements      : this.elements,
                            clientSecret  : clientSecret,
                            confirmParams : {
                                return_url          : successUrl,
                                payment_method_data : {
                                    billing_details : {
                                        email   : this.contact.email || '',
                                        name    : this.cardName.trim(),
                                        address : {
                                            country     : this.contact.country     || 'US',
                                            postal_code : this.contact.postal_code || '',
                                        },
                                    },
                                },
                            },
                            redirect : 'if_required',
                        });
                    }
                }

                const { error, paymentIntent, setupIntent } = result;

                if( error ) {
                    this.cardError = error.message || ( this.isTrial ? 'Setup failed' : 'Payment declined' );
                    this.$emit( 'loading', false );
                    return;
                }

                // Без 3DS - интент готов сразу, редиректим; с 3DS Stripe сам
                // вернёт через return_url. requires_capture - тоже успех (донор).
                const intent = paymentIntent || setupIntent;

                if( intent && [ 'succeeded', 'requires_capture' ].includes( intent.status ) )
                    window.location.href = successUrl;

            } catch( err ) {

                this.$emit( 'error', err?.response?.data?.data?.message
                    || err?.response?.data?.message
                    || err.message
                    || ( this.isTrial ? 'Setup failed. Please try again.' : 'Payment failed. Please try again.' ) );

                this.$emit( 'loading', false );
            }
        }
    },
};
</script>